/* eslint react-hooks/exhaustive-deps: 0 */
import { asArray } from "@/lib/asArray";
import SignOffBar from "@/components/SignOffBar";   // Batch 11 · A2
import React, { useEffect, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useEngagement } from "@/contexts/EngagementContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import { format, parse } from "date-fns";
import { ddmmmyyyy } from "@/lib/dt";   // STEP 3 · B-7c: the one formatter

const CODES = [
  "a_accounting_estimates", "b_related_parties",
  "c_significant_unusual_complex", "d_fraud_or_noncompliance",
  "e_subsequent_events", "f_gc_basis_management",
  "g_gc_events_conditions", "h_commitments_obligations",
  "i_nonmonetary_transactions",
];

export default function W174InquiriesPage() {
  const { current, currentEngId } = useEngagement();
  const { t } = useI18n();
  const [state, setState] = useState(null);
  const reload = async () => {
    if (!currentEngId) return;
    const r = await api.get(`/engagements/${currentEngId}/w17/inquiries`, { _silent: true });
    setState(r.data);
  };
  useEffect(() => { reload(); }, [currentEngId]);
  if (!current) return <div className="text-sm text-muted-foreground">{t("common.noEng")}</div>;
  if (!state) return <div className="text-sm text-muted-foreground p-6" data-testid="w17-4-loading">{t("common.loading")}</div>;
  const isReview = (current?.engagement_type || "audit") === "review";
  if (!isReview) return <div className="p-6 text-sm text-muted-foreground" data-testid="w17-4-audit-only">{t("w17_4.notForAudit")}</div>;
  return (
    <div className="space-y-6" data-testid="w17-4-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" data-testid="w17-4-title">
          <span dir="ltr" className="font-mono text-[13px] text-muted-foreground me-2">504(r)</span>
          {t("w17_4.title")}
        </h1>
        <SignOffBar itemType="w17_4" itemKey="W17.4" />   {/* Batch 11 · A2 */}
        <p className="text-sm text-muted-foreground">{t("w17_4.subtitle")}</p>
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base normal-case">
          {state.all_answered
            ? <span data-testid="w17-4-complete-badge" className="text-emerald-700">{t("w17_4.allAnswered")}</span>
            : <span data-testid="w17-4-incomplete-badge" className="text-amber-800">{t("w17_4.pending")}</span>}
        </CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {CODES.map(c => {
            const r = asArray(state.inquiries).find(x => x.code === c) || { code: c };
            return <InquiryRow key={c} row={r} eng={currentEngId} onSaved={reload} t={t} />;
          })}
        </CardContent>
      </Card>
    </div>
  );
}

function InquiryRow({ row, eng, onSaved, t }) {
  const [d, setD] = React.useState({
    asked_of: row.asked_of || "", date: row.date || "",
    response: row.response || "",
    followup_required: !!row.followup_required,
  });
  React.useEffect(() => { setD({
    asked_of: row.asked_of || "", date: row.date || "",
    response: row.response || "",
    followup_required: !!row.followup_required,
  }); }, [row.code]);
  const save = async () => {
    if (!d.response.trim()) { toast.error(t("w17_4.responseRequired")); return; }
    try {
      await api.post(`/engagements/${eng}/w17/inquiries/answer`,
                          { code: row.code, ...d }, { _silent: true });
      toast.success(t("w17_4.saved"));
      onSaved && onSaved();
    } catch (e) { toast.error(toErrMsg(e, "Failed")); }
  };
  return (
    <div className="rounded border p-2 text-[12px]" data-testid={`w17-4-item-${row.code}`}>
      <div className="mb-1 font-medium">{t(`w17_4.q.${row.code}`)}</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <Input placeholder={t("w17_4.askedOfPh")} value={d.asked_of} onChange={e => setD({...d, asked_of: e.target.value})} data-testid={`w17-4-item-${row.code}-askedof`} />
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="justify-start text-[11px] h-8"
                     data-testid={`w17-4-item-${row.code}-date`}>
              <CalendarIcon className="me-2 h-3 w-3" />
              {d.date
                ? ddmmmyyyy(d.date)
                : t("w17_4.pickDate")}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar mode="single"
                        selected={d.date ? parse(d.date, "yyyy-MM-dd", new Date()) : undefined}
                        onSelect={(dt) => setD({...d, date: dt ? format(dt, "yyyy-MM-dd") : ""})} />
          </PopoverContent>
        </Popover>        <label className="flex items-center gap-2 text-[11px]">
          <Checkbox checked={d.followup_required} onCheckedChange={v => setD({...d, followup_required: !!v})}
                          data-testid={`w17-4-item-${row.code}-followup`} />
          {t("w17_4.followupReq")}
        </label>
      </div>
      <Textarea placeholder={t("w17_4.responsePh")} value={d.response} onChange={e => setD({...d, response: e.target.value})} className="mt-2 h-16 text-[11px]" data-testid={`w17-4-item-${row.code}-response`} />
      {row.followup_register_id && (
        <div className="mt-1 text-[10px] text-emerald-800" data-testid={`w17-4-item-${row.code}-linkto7`}>
          {t("w17_4.linkedRegister")}
        </div>
      )}
      <Button size="sm" className="mt-2" onClick={save} data-testid={`w17-4-item-${row.code}-save`}>{t("w17_4.save")}</Button>
    </div>
  );
}
